import { Avatar, Badge, Tooltip } from '@mantine/core';
import { clsx } from 'clsx';
import { useAtomValue } from 'jotai';
import { isMultitabTestAtom } from '~/atoms/no-code-atoms';
import type { SelectedStep } from '~/server/logic/prisma/step-select';
import NewOperationControl from '../../NewOperationControl';
import { DEFAULT_EMPTY_PREV_OPERATION_ORDER } from '../../OperationList';
import { IconStrikethrough, IconArrowRight } from '@tabler/icons-react';
import type { SelectedOperation } from '~/server/logic/prisma/operation-select';
import type {
  ConditionalBlock as ConditionalBlockType,
  OperationParentContext,
} from '~/server/logic/steps/operation/types';
import Operation from '../../Operation';

export function ConditionalBlock({
  operations,
  conditional,
  testContentId,
  step,
  readonly,
  resolvedToThisBlock,
  parentContexts,
}: {
  operations: SelectedOperation[];
  conditional: ConditionalBlockType;
  testContentId: string;
  step: SelectedStep;
  readonly?: boolean;
  resolvedToThisBlock?: boolean;
  parentContexts?: OperationParentContext[];
}) {
  const hasMultipleTabs = useAtomValue(isMultitabTestAtom);

  return (
    <div className="flex flex-col gap-3">
      <div className="relative">
        {resolvedToThisBlock === true && (
          <div className="animate-in slide-in-from-left absolute -left-5 top-4">
            <IconArrowRight
              className="text-green-600 dark:text-green-400"
              size={16}
            />
          </div>
        )}
        <div
          className={clsx(
            'pb-4 pr-4 pt-4',
            resolvedToThisBlock === undefined ? ''
            : resolvedToThisBlock ? 'border-l-4 border-l-green-500'
            : 'opacity-50',
          )}
        >
          <div className="relative">
            <div className="ml-1 pl-1">
              <Badge
                size="xs"
                variant="filled"
                color={conditional.blockType === 'then' ? 'green' : 'gray'}
                className="ml-2 text-xs"
              >
                {conditional.blockType}
              </Badge>
              {operations?.length ?
                <div>
                  {operations.map((operation, index) => (
                    <div key={operation.id}>
                      <NewOperationControl
                        testContentId={testContentId}
                        prevOperationId={index > 0 ? operations[index - 1].id : undefined}
                        nextOperationId={operation.id}
                        stepId={step.id}
                        stepOrder={step.stepOrder}
                        stepMode={step.mode}
                        insertAfterOperationOrder={
                          index > 0 ? operations[index - 1].order : DEFAULT_EMPTY_PREV_OPERATION_ORDER
                        }
                        operationId={index > 0 ? operations[index - 1].id : undefined}
                        activePageAlias={
                          index > 0 ?
                            operations[index - 1].opensPageAlias || operations[index - 1].pageAlias
                          : operations[0].pageAlias
                        }
                        parentContexts={parentContexts}
                        readonly={readonly}
                      />
                      <div
                        className={clsx('flex items-center gap-2', {
                          'opacity-50': operation.skip,
                        })}
                      >
                        {!!operation.skip && (
                          <Tooltip label="Operation will be skipped">
                            <Avatar
                              size="xs"
                              radius="xl"
                            >
                              <IconStrikethrough size={12} />
                            </Avatar>
                          </Tooltip>
                        )}

                        <Operation
                          step={step}
                          operation={operation}
                          testContentId={testContentId}
                          stepIdx={step.stepOrder}
                          readonly={readonly}
                          parentContexts={parentContexts}
                          showTabLabel={
                            !!hasMultipleTabs &&
                            operation.type !== 'DEFINE_VARIABLE' &&
                            operation.type !== 'RECEIVE_EMAIL' &&
                            operation.type !== 'WAIT'
                          }
                        />
                      </div>
                    </div>
                  ))}
                  <NewOperationControl
                    testContentId={testContentId}
                    prevOperationId={operations.at(-1)?.id}
                    stepId={step.id}
                    stepOrder={step.stepOrder}
                    stepMode={step.mode}
                    insertAfterOperationOrder={operations.at(-1)?.order ?? DEFAULT_EMPTY_PREV_OPERATION_ORDER}
                    operationId={operations.at(-1)?.id}
                    activePageAlias={operations.at(-1)?.opensPageAlias || operations.at(-1)?.pageAlias}
                    parentContexts={parentContexts}
                    readonly={readonly}
                  />
                </div>
              : <div>
                  <div className="ml-7 mt-3 text-sm text-gray-500">No actions</div>
                  <NewOperationControl
                    testContentId={testContentId}
                    stepId={step.id}
                    stepOrder={step.stepOrder}
                    stepMode={step.mode}
                    insertAfterOperationOrder={DEFAULT_EMPTY_PREV_OPERATION_ORDER}
                    activePageAlias="page"
                    parentContexts={parentContexts}
                    readonly={readonly}
                  />
                </div>
              }
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
